import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n, type OrderStatus } from '@wms/shared';
import { api } from '../lib/api.js';
import { type SortDir, useTableSort } from '../lib/sortRows.js';
import { OrderStatusChip } from '../components/StatusChip.js';
import { SavedFilters } from '../components/SavedFilters.js';
import { OrderStageStamp, type OrderStageChange } from '../components/OrderStageStamp.js';

const { t } = i18n;

interface OrderRow {
  id: string;
  code: string;
  status: OrderStatus;
  insufficientParts: boolean;
  placedAt: string;
  assembledAt: string | null;
  deliveredAt: string | null;
  closedAt: string | null;
  partnerProjectRef: string | null;
  partnerProjectName: string | null;
  partner: { code: string; name: string };
  changes: OrderStageChange[];
  _count: { lines: number; deliveryGroups: number };
}

const ORDER_SORT_ACCESSORS = {
  partner: (row: OrderRow) => row.partner.code,
  code: (row: OrderRow) => row.code,
  project_ref: (row: OrderRow) => row.partnerProjectRef,
  project_name: (row: OrderRow) => row.partnerProjectName,
  placed_at: (row: OrderRow) => row.placedAt,
  assembled_at: (row: OrderRow) => row.assembledAt,
  delivered_at: (row: OrderRow) => row.deliveredAt,
  closed_at: (row: OrderRow) => row.closedAt,
  status: (row: OrderRow) => t(`order.status.${row.status}`),
} as const;

interface SortCtl {
  sortKey: string;
  sortDir: SortDir;
  toggle: (key: string) => void;
}

function SortableTh({ label, col, sort }: { label: string; col: string; sort: SortCtl }) {
  const active = sort.sortKey === col;
  return (
    <th
      className="px-3 py-2 font-medium"
      aria-sort={active ? (sort.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => sort.toggle(col)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        aria-label={label}
      >
        <span>{label}</span>
        <span className="text-[10px] text-muted-foreground" aria-hidden="true">
          {active ? (sort.sortDir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

const STATUS_OPTIONS: (OrderStatus | 'ALL')[] = [
  'ALL',
  'DRAFT',
  'SUBMITTED',
  'ON_HOLD',
  'IN_ASSEMBLY',
  'ASSEMBLED',
  'PACKING_ON_PALLET',
  'READY_FOR_SHIPMENT',
  'DELIVERED',
  'CLOSED',
  'CANCELLED',
];

const PARTNER_ROW_TONES = [
  'bg-sky-50/80 dark:bg-sky-950/30',
  'bg-emerald-50/80 dark:bg-emerald-950/30',
  'bg-amber-50/80 dark:bg-amber-950/30',
  'bg-violet-50/80 dark:bg-violet-950/30',
  'bg-rose-50/80 dark:bg-rose-950/30',
  'bg-cyan-50/80 dark:bg-cyan-950/30',
  'bg-lime-50/80 dark:bg-lime-950/30',
  'bg-orange-50/80 dark:bg-orange-950/30',
] as const;

interface PartnerOpt {
  id: string;
  code: string;
  name: string;
}

export function OrdersPage() {
  const [rows, setRows] = useState<OrderRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const [insufficientOnly, setInsufficientOnly] = useState(false);
  const [partnerFilter, setPartnerFilter] = useState<string>('');
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [placedFrom, setPlacedFrom] = useState<string>('');
  const [placedTo, setPlacedTo] = useState<string>('');
  const [addressKeyword, setAddressKeyword] = useState<string>('');
  // Server-side text search (WMS-34): order number, project ref, project name.
  // `search` is the live input; `debouncedSearch` is what actually drives the
  // fetch, so we don't request on every keystroke (review P2).
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    api<PartnerOpt[]>('/partners')
      .then(setPartners)
      .catch(() => setPartners([]));
  }, []);

  useEffect(() => {
    // Generation guard: if the inputs change before this request resolves, the
    // cleanup flips `ignore`, so a late/out-of-order response can't overwrite
    // the newer results (review P2).
    let ignore = false;
    setRows(null);
    // Clear any previous error so a successful retry after a transient failure
    // recovers instead of staying stuck on the old message (review P2).
    setError(null);
    const params = new URLSearchParams();
    if (statusFilter !== 'ALL') params.set('status', statusFilter);
    if (insufficientOnly) params.set('insufficientOnly', 'true');
    if (partnerFilter) params.set('partnerId', partnerFilter);
    if (placedFrom) params.set('placedFrom', placedFrom);
    if (placedTo) params.set('placedTo', placedTo);
    if (addressKeyword.trim()) params.set('addressKeyword', addressKeyword.trim());
    if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
    const qs = params.toString();
    api<OrderRow[]>(`/orders${qs ? `?${qs}` : ''}`)
      .then((r) => {
        if (!ignore) setRows(r);
      })
      .catch((err: unknown) => {
        if (!ignore) setError(String(err));
      });
    return () => {
      ignore = true;
    };
  }, [
    statusFilter,
    insufficientOnly,
    partnerFilter,
    placedFrom,
    placedTo,
    addressKeyword,
    debouncedSearch,
  ]);

  // Rows already reflect the server-side search + filters (WMS-34 review P1).
  const orderSort = useTableSort(rows ?? [], ORDER_SORT_ACCESSORS, {
    key: 'placed_at',
    dir: 'desc',
  });
  const visible = orderSort.sorted;
  const partnerRowTones = useMemo(() => {
    const partnerCodes = [...new Set((rows ?? []).map((row) => row.partner.code))].sort((a, b) =>
      a.localeCompare(b, 'lt'),
    );
    if (partnerCodes.length < 2) return new Map<string, string>();
    return new Map(
      partnerCodes.map((code, index) => [
        code,
        PARTNER_ROW_TONES[index % PARTNER_ROW_TONES.length]!,
      ]),
    );
  }, [rows]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">{t('order.list.title')}</h2>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <a
            href="/partnerio-uzsakymu-vadovas-lt.pdf"
            target="_blank"
            rel="noreferrer"
            className="rounded border border-border px-3 py-1.5 text-sm hover:bg-muted"
          >
            {t('order.list.partner_manual')}
          </a>
          <Link
            to="/orders/new"
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
          >
            {t('order.list.create')}
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('order.filter.search')}
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('order.filter.search_hint')}
            aria-label={t('order.filter.search_hint')}
            className="rounded border border-border bg-background px-2 py-1 w-56"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('order.col.status')}
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as OrderStatus | 'ALL')}
            className="rounded border border-border bg-background px-2 py-1"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s === 'ALL' ? t('order.filter.all') : t(`order.status.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('order.col.partner')}
          </span>
          <select
            value={partnerFilter}
            onChange={(e) => setPartnerFilter(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          >
            <option value="">{t('order.filter.all')}</option>
            {partners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('order.filter.placed_from')}
          </span>
          <input
            type="date"
            value={placedFrom}
            onChange={(e) => setPlacedFrom(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('order.filter.placed_to')}
          </span>
          <input
            type="date"
            value={placedTo}
            onChange={(e) => setPlacedTo(e.target.value)}
            className="rounded border border-border bg-background px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">
            {t('order.filter.address')}
          </span>
          <input
            type="search"
            value={addressKeyword}
            onChange={(e) => setAddressKeyword(e.target.value)}
            placeholder={t('order.filter.address_hint')}
            className="rounded border border-border bg-background px-2 py-1"
          />
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={insufficientOnly}
            onChange={(e) => setInsufficientOnly(e.target.checked)}
          />
          {t('order.filter.insufficient_only')}
        </label>
      </div>

      <SavedFilters
        scope="ORDERS"
        current={{
          status: statusFilter === 'ALL' ? null : statusFilter,
          insufficientOnly: insufficientOnly ? true : null,
          partnerId: partnerFilter || null,
          placedFrom: placedFrom || null,
          placedTo: placedTo || null,
          addressKeyword: addressKeyword || null,
        }}
        apply={(p) => {
          setStatusFilter(p.status ?? 'ALL');
          setInsufficientOnly(p.insufficientOnly === true);
          setPartnerFilter(p.partnerId ?? '');
          setPlacedFrom(p.placedFrom ?? '');
          setPlacedTo(p.placedTo ?? '');
          setAddressKeyword(p.addressKeyword ?? '');
        }}
      />

      {rows === null ? (
        <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : visible.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t('order.list.empty')}
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <table className="w-max whitespace-nowrap text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <SortableTh label={t('order.col.partner')} col="partner" sort={orderSort} />
                  <SortableTh label={t('order.col.code')} col="code" sort={orderSort} />
                  <SortableTh
                    label={t('order.col.project_ref')}
                    col="project_ref"
                    sort={orderSort}
                  />
                  <SortableTh
                    label={t('order.col.project_name')}
                    col="project_name"
                    sort={orderSort}
                  />
                  <SortableTh label={t('order.col.placed_at')} col="placed_at" sort={orderSort} />
                  <SortableTh
                    label={t('order.col.assembled_at')}
                    col="assembled_at"
                    sort={orderSort}
                  />
                  <SortableTh
                    label={t('order.col.delivered_at')}
                    col="delivered_at"
                    sort={orderSort}
                  />
                  <SortableTh label={t('order.col.closed_at')} col="closed_at" sort={orderSort} />
                  <SortableTh label={t('order.col.status')} col="status" sort={orderSort} />
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-t border-border ${partnerRowTones.get(row.partner.code) ?? ''}`}
                    data-partner-code={row.partner.code}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{row.partner.code}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link
                        to={`/orders/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {row.code}
                      </Link>
                      {row.insufficientParts ? (
                        <span
                          className="ml-2 inline-flex items-center rounded-full border border-red-300 bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-900 dark:border-red-700 dark:bg-red-900/40 dark:text-red-100"
                          title={t('order.flag.insufficient_parts')}
                        >
                          !
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs">{row.partnerProjectRef ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">{row.partnerProjectName ?? '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      <OrderStageStamp value={row.placedAt} changes={row.changes} status="DRAFT" />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <OrderStageStamp
                        value={row.assembledAt}
                        changes={row.changes}
                        status="ASSEMBLED"
                      />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <OrderStageStamp
                        value={row.deliveredAt}
                        changes={row.changes}
                        status="DELIVERED"
                      />
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <OrderStageStamp value={row.closedAt} changes={row.changes} status="CLOSED" />
                    </td>
                    <td className="px-3 py-2">
                      <OrderStatusChip status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="md:hidden space-y-2">
            {visible.map((row) => (
              <li
                key={row.id}
                className={`rounded-md border border-border p-3 space-y-2 ${partnerRowTones.get(row.partner.code) ?? ''}`}
                data-partner-code={row.partner.code}
              >
                <div className="flex items-start justify-between gap-2">
                  <Link
                    to={`/orders/${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-sm hover:underline"
                  >
                    {row.code}
                    {row.insufficientParts ? (
                      <span
                        className="ml-2 inline-flex items-center rounded-full border border-red-300 bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-900 dark:border-red-700 dark:bg-red-900/40 dark:text-red-100"
                        title={t('order.flag.insufficient_parts')}
                      >
                        !
                      </span>
                    ) : null}
                  </Link>
                  <OrderStatusChip status={row.status} />
                </div>
                <div className="font-mono text-xs text-muted-foreground">{row.partner.code}</div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <dt className="text-muted-foreground">{t('order.col.project_ref')}</dt>
                  <dd className="text-right">{row.partnerProjectRef ?? '—'}</dd>
                  <dt className="text-muted-foreground">{t('order.col.project_name')}</dt>
                  <dd className="text-right">{row.partnerProjectName ?? '—'}</dd>
                  <dt className="text-muted-foreground">{t('order.col.placed_at')}</dt>
                  <dd className="text-right">
                    <OrderStageStamp value={row.placedAt} changes={row.changes} status="DRAFT" />
                  </dd>
                  <dt className="text-muted-foreground">{t('order.col.assembled_at')}</dt>
                  <dd className="text-right">
                    <OrderStageStamp
                      value={row.assembledAt}
                      changes={row.changes}
                      status="ASSEMBLED"
                    />
                  </dd>
                  <dt className="text-muted-foreground">{t('order.col.delivered_at')}</dt>
                  <dd className="text-right">
                    <OrderStageStamp
                      value={row.deliveredAt}
                      changes={row.changes}
                      status="DELIVERED"
                    />
                  </dd>
                  <dt className="text-muted-foreground">{t('order.col.closed_at')}</dt>
                  <dd className="text-right">
                    <OrderStageStamp value={row.closedAt} changes={row.changes} status="CLOSED" />
                  </dd>
                </dl>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
